/**
 * InstallPrompt — Phase 3.8 PWA "Add to Home Screen" CTA.
 *
 * - Android/Chrome: listens for `beforeinstallprompt`, presents a
 *   compact button that calls deferred.prompt() on click.
 * - iOS Safari: detects iOS UA and shows simple "Tap Share → Add to
 *   Home Screen" instructions in a dismissible sheet.
 * - Already installed (standalone): renders nothing.
 * - Dismissed (localStorage flag) for 30 days: renders nothing.
 *
 * Does NOT show during active match (parent decides via `hidden` prop).
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Share } from 'lucide-react';

const DISMISS_KEY = 'kickard.pwa.installDismissedAt';
const DISMISS_TTL_MS = 30 * 24 * 3600 * 1000; // 30 days

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS-specific
    window.navigator?.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const t = parseInt(raw, 10);
    if (!Number.isFinite(t)) return false;
    return Date.now() - t < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

export default function InstallPrompt({ hidden = false, fallback = null, label = 'Install Kickard' }) {
  const [deferred, setDeferred] = useState(null);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(() => wasDismissedRecently());

  useEffect(() => {
    if (installed) return undefined;
    const onPrompt = (e) => {
      // Stop Chrome's mini-infobar; we render our own button.
      e.preventDefault?.();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [installed]);

  if (hidden) return null;
  // Optional status for hosts (Settings); default callers still render nothing.
  if (installed) return fallback ? fallback('installed') : null;
  if (dismissed) return fallback ? fallback('unavailable') : null;

  const onClick = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        setDeferred(null);
        if (choice?.outcome === 'accepted') {
          setInstalled(true);
        } else {
          markDismissed();
          setDismissed(true);
        }
      } catch {
        // ignore
      }
      return;
    }
    if (isIOS()) {
      setShowIosSheet(true);
      return;
    }
    // No prompt available + not iOS — silently dismiss.
    markDismissed();
    setDismissed(true);
  };

  const showButton = deferred || isIOS();
  if (!showButton) return fallback ? fallback('unavailable') : null;

  return (
    <>
      <button
        type="button"
        data-testid="pwa-install-btn"
        onClick={onClick}
        aria-label="Install Kickard on your device"
        className="cta-pill py-2 text-[11px] flex items-center justify-center gap-1.5 mt-1"
      >
        <Download size={14} strokeWidth={2.25} />
        <span>{label}</span>
      </button>

      <AnimatePresence>
        {showIosSheet && (
          <motion.div
            key="ios-sheet"
            data-testid="pwa-ios-sheet"
            className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-end justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowIosSheet(false)}
          >
            <motion.div
              initial={{ y: 60 }}
              animate={{ y: 0 }}
              exit={{ y: 60 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className="w-full max-w-[440px] mx-auto rounded-t-2xl border-t border-[hsl(var(--k-gold)/0.35)] bg-[hsl(220,12%,8%)]"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10">
                <div className="font-heading text-base tracking-[0.18em] uppercase text-white">
                  Add to Home Screen
                </div>
                <button
                  type="button"
                  data-testid="pwa-ios-sheet-close"
                  aria-label="Close"
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white/75 hover:text-white border border-white/15 active:scale-95"
                  onClick={() => { setShowIosSheet(false); markDismissed(); setDismissed(true); }}
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div className="font-body text-sm text-white/85 leading-snug">
                  Install Kickard on your iPhone for a full-screen, app-like experience.
                </div>
                <ol className="font-body text-sm text-white/80 space-y-2 list-decimal pl-5">
                  <li>
                    Tap the{' '}
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-white">
                      <Share size={13} strokeWidth={2.25} /> Share
                    </span>{' '}
                    icon in Safari.
                  </li>
                  <li>Choose <span className="font-heading tracking-[0.12em] text-[hsl(var(--k-gold-bright))]">Add to Home Screen</span>.</li>
                  <li>Tap <span className="font-heading tracking-[0.12em] text-[hsl(var(--k-gold-bright))]">Add</span>.</li>
                </ol>
                <div className="text-[11px] text-white/45 pt-1">
                  Works on iOS 16.4+ in Safari.
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
